import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { RichEditor } from "@/admin/RichEditor";
import { ImageField, ImagesField, ColorField, SliderField } from "@/admin/fields";

const CategoryEditor = ({ value, onChange }) => {
  const set = (i, k, v) => onChange(value.map((c, j) => (j === i ? { ...c, [k]: v } : c)));
  return (
    <div className="space-y-6">
      {value.map((c, i) => (
        <div key={c.slug} className="rounded-xl border bg-ivory p-5 space-y-4" data-testid={`category-editor-${c.slug}`}>
          <div className="flex items-center justify-between">
            <p className="text-xs font-bold uppercase tracking-wider text-brand">Category {i + 1}{i < 2 ? " · image below text" : " · image on right"}</p>
            <div className="flex items-center gap-2 text-xs">Show<Switch checked={!c.hidden} onCheckedChange={(v) => set(i, "hidden", !v)} data-testid={`category-visible-${c.slug}`} /></div>
          </div>
          <Field f={{ k: "title", label: "Title", type: "text" }} value={c.title} onChange={(v) => set(i, "title", v)} id={`cat-${c.slug}-title`} />
          <Field f={{ k: "description", label: "Description", type: "textarea" }} value={c.description} onChange={(v) => set(i, "description", v)} id={`cat-${c.slug}-description`} />
          <Field f={{ k: "highlights_label", label: "Highlights label", type: "text" }} value={c.highlights_label} onChange={(v) => set(i, "highlights_label", v)} id={`cat-${c.slug}-hl-label`} />
          <Field f={{ k: "highlights", label: "Highlights", type: "textarea", help: "One per line" }} value={c.highlights} onChange={(v) => set(i, "highlights", v)} id={`cat-${c.slug}-highlights`} />
          <div className="grid sm:grid-cols-2 gap-4">
            <Field f={{ k: "price_label", label: "Price box label", type: "text" }} value={c.price_label} onChange={(v) => set(i, "price_label", v)} id={`cat-${c.slug}-price-label`} />
            <Field f={{ k: "price", label: "Price", type: "text" }} value={c.price} onChange={(v) => set(i, "price", v)} id={`cat-${c.slug}-price`} />
          </div>
          <Field f={{ k: "button", label: "Button label", type: "text" }} value={c.button} onChange={(v) => set(i, "button", v)} id={`cat-${c.slug}-button`} />
          <Field f={{ k: "image", label: "Sample image (hidden if empty)", type: "image" }} value={c.image} onChange={(v) => set(i, "image", v)} id={`cat-${c.slug}-image`} />
          <Field f={{ k: "image_width", label: "Sample image size", type: "slider", min: 10, max: 100, step: 5, suffix: "%" }} value={c.image_width} onChange={(v) => set(i, "image_width", v)} id={`cat-${c.slug}-image-width`} />
        </div>
      ))}
    </div>
  );
};

export const Field = ({ f, value, onChange, id }) => {
  const control = {
    text: () => <Input value={value ?? ""} onChange={(e) => onChange(e.target.value)} className="bg-white" data-testid={id} />,
    number: () => <Input type="number" value={value ?? ""} onChange={(e) => onChange(Number(e.target.value))} className="bg-white w-40" data-testid={id} />,
    textarea: () => <Textarea value={value ?? ""} onChange={(e) => onChange(e.target.value)} rows={4} className="bg-white" data-testid={id} />,
    rich: () => <RichEditor value={value} onChange={onChange} testId={id} />,
    image: () => <ImageField value={value} onChange={onChange} testId={id} />,
    pdf: () => <ImageField value={value} onChange={onChange} testId={id} accept="application/pdf" />,
    images: () => <ImagesField value={value || []} onChange={onChange} testId={id} />,
    color: () => <ColorField value={value} onChange={onChange} testId={id} />,
    slider: () => <SliderField value={value} onChange={onChange} min={f.min} max={f.max} step={f.step} suffix={f.suffix} testId={id} />,
    categories: () => <CategoryEditor value={value || []} onChange={onChange} />,
  }[f.type];
  return (
    <div className="space-y-1.5">
      <Label className="text-sm font-semibold">{f.label}</Label>
      {f.help && <p className="text-xs text-muted-foreground">{f.help}</p>}
      {control()}
    </div>
  );
};
